// SPDX-License-Identifier: MIT
// The « pourquoi » affordance (guided first-run). Each surfaced piece carries a
// warm, jargon-free micro-explanation: auto-open the first time a piece is
// revealed, then collapses to a recallable "?" pill. Seen-state persists per
// piece in localStorage (genesis.why.<pieceId>) so it auto-shows only once.
// Copy lives in i18n (onboarding.why.<pieceId>); a `body` override carries the
// file-grounded « pourquoi » in the deep-discovery beat. Styles: guided-first-run.css.
import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';

const SEEN_PREFIX = 'genesis.why.';

export function whySeen(pieceId: string): boolean {
  try { return localStorage.getItem(SEEN_PREFIX + pieceId) === '1'; } catch { return false; }
}
export function markWhySeen(pieceId: string): void {
  try { localStorage.setItem(SEEN_PREFIX + pieceId, '1'); } catch { /* storage off — harmless */ }
}

export interface WhyBubbleOptions {
  pieceId: string;        // key into onboarding.why.<pieceId>
  autoOpen?: boolean;     // expanded vs collapsed at mount (caller: autoOpen = !whySeen(id))
  label?: string;         // the lab text (default: t('onboarding.whyLabel'))
  body?: string;          // copy override (default: t('onboarding.why.<pieceId>'))
}

// __collapse() is a programmatic handle for the current-only fold: it renders
// the "?" pill WITHOUT calling markWhySeen — advancing to the next question
// is not the same as the user dismissing the bubble.
export type WhyBubbleElement = HTMLElement & { __collapse(): void };

export function renderWhyBubble(opts: WhyBubbleOptions): WhyBubbleElement {
  const root = document.createElement('div');
  root.className = 'why-bubble';
  root.setAttribute('data-component', 'why-bubble');
  const label = opts.label ?? t('onboarding.whyLabel');
  const body = opts.body ?? t('onboarding.why.' + opts.pieceId);

  const expand = (): void => {
    root.innerHTML = `
      <div class="why">
        <div class="lab">${escapeHtml(label)}<button type="button" class="x" data-component="why-dismiss" aria-label="${escapeHtml(t('onboarding.whyClose'))}">✕</button></div>
        <p>${escapeHtml(body)}</p>
      </div>`;
    root.querySelector<HTMLButtonElement>('[data-component="why-dismiss"]')!.addEventListener('click', () => {
      markWhySeen(opts.pieceId);
      collapse();
    });
  };
  const collapse = (): void => {
    // keep BOTH classes — `.why--seen` styles ride on the `.why` base (border/bg).
    root.innerHTML = `
      <button type="button" class="why why--seen" data-component="why-recall">
        <span class="q">?</span><span>${escapeHtml(label)}</span>
      </button>`;
    root.querySelector<HTMLButtonElement>('[data-component="why-recall"]')!.addEventListener('click', expand);
  };

  if (opts.autoOpen) expand(); else collapse();
  // `root` is HTMLDivElement, a strict subtype of HTMLElement — TS won't allow
  // a direct cast to an intersection adding a required member, hence the
  // unknown hop (this is the compiler's own suggested fix, see TS2352).
  const el = root as unknown as WhyBubbleElement;
  el.__collapse = collapse;
  return el;
}
